import React, { useEffect, useRef } from "react";
import { Box, Typography, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import * as routes from "../../../constants/routes";
import coinIcon from "../../../assets/images/icons/iconCoinPanel.svg";
import personIcon from "../../../assets/images/icons/iconPersonPanel.svg";
import AvatarManager from "components/Avatar/AvatarManager";
import useFetch from "../../../hooks/useFetch";
import { API_Q_PARENT } from "../../../constants/api";
import { motion, animate } from "framer-motion";

const HeaderContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#662D91",
  borderRadius: "0.75rem",
  padding: "3rem",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: "1.5rem",
  marginBottom: "1.25rem",
  width: "100%",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: "1rem",
    padding: "1.5rem",
  },
}));

const UserSection = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  [theme.breakpoints.down("sm")]: {
    gap: "12px",
    paddingBottom: "1rem",
    borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
  },
}));

const UserInfo = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "2px",
});

const UserName = styled(Typography)(({ theme }) => ({
  fontFamily: "Montserrat, sans-serif",
  fontWeight: 600,
  fontSize: "1.526rem",
  lineHeight: "100%",
  letterSpacing: "0",
  color: "var(--color-gray-10, #FFFFFF)",
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.125rem",
  },
}));

const UserGreeting = styled(Typography)(({ theme }) => ({
  fontFamily: "Montserrat, sans-serif",
  fontWeight: 500,
  fontSize: "1.144rem",
  lineHeight: "100%",
  letterSpacing: "0",
  color: "#C9B1D9 ",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.875rem",
  },
}));

const CoinsSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "0.25rem",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "1rem",
    cursor: "default",
  },
}));

const CoinsRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  [theme.breakpoints.down("sm")]: {
    gap: "0.375rem",
  },
}));

const CoinIcon = styled("img")(({ theme }) => ({
  width: "24px",
  height: "24px",
  [theme.breakpoints.down("sm")]: {
    width: "20px",
    height: "20px",
  },
}));

const CoinValue = styled(Typography)({
  fontFamily: "Montserrat, sans-serif",
  fontWeight: 600,
  fontSize: "1.25rem",
  lineHeight: "100%",
  letterSpacing: "0",
  textAlign: "center",
  color: "#FFCA55",
});

const CoinLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "Montserrat, sans-serif",
  fontWeight: 500,
  fontSize: "0.875rem",
  lineHeight: "100%",
  letterSpacing: "0",
  textAlign: "center",
  textTransform: "uppercase",
  color: "#FFFFFF",
  [theme.breakpoints.down("sm")]: {
    textAlign: "left",
    fontSize: "0.75rem",
  },
}));

const PlayButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#FFFFFF",
  color: "#662D91",
  borderRadius: "50px",
  padding: "10px 24px",
  fontFamily: "Montserrat, sans-serif",
  fontWeight: 700,
  fontSize: "14px",
  textTransform: "uppercase",
  display: "none",
  "&:hover": {
    backgroundColor: "#f5f5f5",
  },
  [theme.breakpoints.down("sm")]: {
    display: "block",
  },
}));

const CoinsInfo = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "0.25rem",
  cursor: "pointer",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "center",
    gap: "0.5rem",
  },
}));

const DashboardHeader = ({ currentUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeUser, session } = useSelector((state) => state.session);
  const isViewingPatient = !!activeUser;
  const isProfessional =
    !isViewingPatient && session?.my_user?.profile === "professional";
  const isPatient = isViewingPatient || session?.my_user?.profile === "patient";
  const usersCount = session?.my_user?.count_patient ?? 90;

  const userName =
    activeUser?.given_name ||
    session?.my_user?.given_name ||
    session?.given_name;

  const parentSub = session?.my_user?.parent_sub;
  const hasLink = !!parentSub || isViewingPatient;
  const [myParentUser, isLoadingParent, , fetchMyParentUser] = useFetch({
    autoInit: false,
  });

  useEffect(() => {
    if (parentSub && !isViewingPatient) {
      fetchMyParentUser({ url: API_Q_PARENT });
    }
  }, [parentSub, isViewingPatient, fetchMyParentUser]);

  const parentName = isViewingPatient
    ? session?.my_user?.given_name
    : isLoadingParent
      ? " "
      : (myParentUser?.given_name ?? "Inserir código");

  const handleCoinsClick = () => {
    navigate(routes.R_COINSTORE);
  };

  const handleUsersClick = () => {
    navigate(routes.R_LIST_MY_USERS);
  };

  const handlePlayClick = () => {
    navigate(routes.R_ACTIVTS);
  };

  const handleLinkClick = () => {
    if (!hasLink) {
      navigate(routes.R_RESCUE_CODE);
    }
  };

  const coins = currentUser?.stars || 0;

  const getCrystalLabel = () => {
    if (coins <= 1) {
      return t("global.crystal");
    }
    return t("global.crystals");
  };
  const nodeRef = useRef(null);

  useEffect(() => {
    const targetValue = Number(coins) || 0;
    // Pega o valor que já está desenhado na tela
    const startValue = Number(nodeRef.current?.textContent) || 0;

    const controls = animate(startValue, targetValue, {
      duration: 3,
      ease: "easeOut",
      onUpdate: (latest) => {
        // Altera diretamente o HTML sem passar pela reconciliação do React
        if (nodeRef.current) {
          nodeRef.current.textContent = Math.round(latest);
        }
      },
    });

    return () => controls.stop();
  }, [coins]);

  return (
    <HeaderContainer>
      <UserSection>
        <AvatarManager />
        <UserInfo>
          <UserName>{userName}</UserName>
          {!isProfessional && (
            <UserGreeting>{t("global.ready_for_training")}</UserGreeting>
          )}
        </UserInfo>
      </UserSection>

      <CoinsSection
        sx={{
          flexDirection: "row",
          gap: "2rem",
        }}
      >
        {isProfessional && (
          <CoinsInfo onClick={handleUsersClick}>
            <CoinsRow>
              <CoinIcon src={personIcon} alt="pessoas" />
              <CoinValue>{usersCount}</CoinValue>
            </CoinsRow>
            <CoinLabel>USUÁRIOS</CoinLabel>
          </CoinsInfo>
        )}
        <CoinsInfo onClick={handleCoinsClick}>
          <CoinsRow>
            <CoinIcon src={coinIcon} alt="Cristais" />
            <CoinValue>
              <Box component={motion.span} ref={nodeRef}>
                0
              </Box>
            </CoinValue>
          </CoinsRow>
          <CoinLabel>{getCrystalLabel()}</CoinLabel>
        </CoinsInfo>
        {isPatient && (
          <CoinsInfo onClick={handleLinkClick}>
            <CoinsRow>
              <CoinValue>VINCULO</CoinValue>
              <CoinIcon src={personIcon} alt="pessoas" />
            </CoinsRow>
            <CoinLabel>{parentName}</CoinLabel>
          </CoinsInfo>
        )}
        <PlayButton onClick={handlePlayClick}>{t("global.play")}</PlayButton>
      </CoinsSection>
    </HeaderContainer>
  );
};

export default DashboardHeader;
